import PaginatedResponse from '@/presentation/pages/resources/PaginatedResponse';
import PlantApi from '@/data/plants/PlantApi';
import type Company from '@/domain/model/business/Company';
import TicketCompanyApi from '@/data/tickets/TicketCompanyApi.ts';

export default class PaginatedItemsHelper {
  private static readonly fieldRelaMap = new Map<string, string[]>([
    ['issues', ['meter_issues']],
    ['current_promise_status', ['promise_statuses']],
    ['status_since', ['promise_statuses.last_change']],
    ['found_on', ['meter_issues', 'issue']],
    ['driver_accounts', ['meters.driver_account']],
    ['attached_companies', ['companies']],
    ['snoozed_until', ['issue']],
    ['new_issue', ['issue']],
    ['existing_issues', ['issue']],
    ['peak_in_watt', ['meters']],
    // one-on-ones
    ['tags', ['tags']],
    ['yield_score_7d', ['yield_score_7d']],
    ['yield_score_30d', ['yield_score_30d']],
    ['custom_fields', ['custom_fields']],
    ['tickets', ['tickets']],
    ['city', ['address']],
    ['street', ['address']],
    ['street_number', ['address']],
    ['postal_code', ['address']],
  ]);

  static mapFieldsToRelations(
    shownFields: Array<string>,
    fieldRelaMap: Map<string, string[]>,
  ): Array<string> {
    let relaArray = shownFields
      .filter((fieldName) => fieldRelaMap.has(fieldName))
      .flatMap((f) => fieldRelaMap.get(f));
    return [...new Set(relaArray)];
  }

  static createPromiseForPaginatedResponseForIssues(
    company: Company,
    params: any,
    shownFields: Array<string>,
  ): Promise<PaginatedResponse> {
    let relations = PaginatedItemsHelper.mapFieldsToRelations(
      shownFields,
      PaginatedItemsHelper.fieldRelaMap,
    );

    return new PlantApi()
      .forwardToErrorPageOnException()
      .getAllPlantIssuesOfCompany(company.getId(), params, relations)
      .then((rsp) => {
        let plantData = rsp.data.data.map((plantJson: any) => ({
          plant: plantJson,
        }));
        return new PaginatedResponse(
          plantData,
          rsp.data.total,
          rsp.data.per_page,
        );
      });
  }

  static createPromiseForPaginatedResponseForPlants(
    company: Company,
    params: Object,
    shownFields: Array<string>,
  ): Promise<PaginatedResponse> {
    let relations = PaginatedItemsHelper.mapFieldsToRelations(
      shownFields,
      PaginatedItemsHelper.fieldRelaMap,
    );

    return new PlantApi()
      .forwardToErrorPageOnException()
      .getAllPlantsOfCompany(company.getId(), params, relations)
      .then((rsp) => {
        let plantData = rsp.data.data.map((plantJson: any) => ({
          plant: plantJson,
        }));
        return new PaginatedResponse(
          plantData,
          rsp.data.total,
          rsp.data.per_page,
        );
      });
  }

  static createPromiseForPaginatedResponseForPerformance(
    company: Company,
    params: any,
    shownFields: Array<string>,
  ): Promise<PaginatedResponse> {
    params.monitored = 0;
    return this.createPromiseForPaginatedResponseForPlants(
      company,
      params,
      shownFields,
    );
  }

  static createPromiseForPaginatedResponseForTickets(
    company: Company,
    params: Object,
    shownFields: Array<string>,
  ): Promise<PaginatedResponse> {
    const fieldRelaMap = new Map<string, string[]>([
      ['issues', ['plant.meter_issues']],
      ['current_promise_status', ['plant.promise_statuses']],
      ['status_since', ['plant.promise_statuses.last_change']],
      ['found_on', ['plant.meter_issues', 'issue']],
      ['driver_accounts', ['plant.meters.driver_account']],
      ['attached_companies', ['plant.companies']],
      ['snoozed_until', ['plant.issue']],
      ['new_issue', ['plant.issue']],
      ['existing_issues', ['plant.issue']],
      ['peak_in_watt', ['plant.meters']],
      // one-on-ones
      ['tags', ['plant.tags']],
      ['yield_score_7d', ['plant.yield_score_7d']],
      ['yield_score_30d', ['plant.yield_score_30d']],
      ['custom_fields', ['plant.custom_fields']],
      ['tickets', ['plant.tickets']],
      ['city', ['plant.address']],
      ['street', ['plant.address']],
      ['street_number', ['plant.address']],
      ['postal_code', ['plant.address']],
      // tickets
      ['assignees', ['assignees']],
    ]);

    let relations = PaginatedItemsHelper.mapFieldsToRelations(
      shownFields,
      fieldRelaMap,
    ).concat(['plant']);

    return new TicketCompanyApi()
      .forwardToErrorPageOnException()
      .getCompanyTickets(company.getId(), params, relations)
      .then((rsp) => {
        return new PaginatedResponse(
          rsp.data.data.map((ticketJson: any) => {
            return {
              ticket: ticketJson,
              plant: ticketJson.plant,
            };
          }),
          rsp.data.total,
          rsp.data.per_page,
        );
      });
  }
}
